/***
*
*   ADD-ONS PANEL
*   Manage add-on products with CRUD operations
*
**********/

import { useState } from 'react';
import { Card, Button, Icon } from 'components/lib';
import Style from './bundlePackaging.tailwind.js';

const PRICING_TYPES = [
  { value: 'total_unit', label: 'Total Unit' },
  { value: 'per_user', label: 'Per User' },
  { value: 'per_org', label: 'Per Workspace' },
  { value: 'fixed', label: 'Fixed Price' },
];

const INTERVAL_OPTIONS = [
  { value: 'month', label: 'Monthly' },
  { value: 'year', label: 'Yearly' },
];

const DEFAULT_ADDON = {
  id: '',
  key: '',
  feature_key: '',
  name: '',
  description: '',
  price: 0,
  price_min: null,
  interval: 'month',
  total_unit: 1,
  unit: '',
  type: 'recurring',
  pricing_type: 'total_unit',
};

export function AddOnsPanel({ addOns, availableFeatures, onSave, onDelete, onCreate, onUpdate, loading }) {
  const [editingAddOn, setEditingAddOn] = useState(null);
  const [isNewAddOn, setIsNewAddOn] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const addOnsList = addOns || {};
  const addOnKeys = Object.keys(addOnsList);

  const handleAddAddOn = () => {
    setEditingAddOn({ ...DEFAULT_ADDON });
    setIsNewAddOn(true);
  };

  const handleEditAddOn = (addOnKey) => {
    const addOn = addOnsList[addOnKey];
    setEditingAddOn({
      ...DEFAULT_ADDON,
      ...addOn,
      key: addOnKey.replace('addon_', ''),
    });
    setIsNewAddOn(false);
  };

  const handleCancelEdit = () => {
    setEditingAddOn(null);
    setIsNewAddOn(false);
  };

  const handleSaveAddOn = () => {
    if (!editingAddOn.name || !editingAddOn.feature_key) {
      return;
    }

    const addOnData = {
      id: editingAddOn.id || `addon_${editingAddOn.key || editingAddOn.feature_key}`,
      feature_key: editingAddOn.feature_key,
      name: editingAddOn.name,
      description: editingAddOn.description,
      pricing_type: editingAddOn.pricing_type,
      type: editingAddOn.type,
      interval: editingAddOn.interval,
      currency: editingAddOn.currency || { name: 'usd', symbol: '$' },
    };

    if (editingAddOn.type === 'one_time') {
      if (editingAddOn.pricing_type === 'fixed') {
        addOnData.price_min = editingAddOn.price_min;
      } else {
        addOnData.price = editingAddOn.price;
      }
    } else {
      addOnData.price = editingAddOn.price;

      if (editingAddOn.pricing_type === 'total_unit' || editingAddOn.pricing_type === 'per_user') {
        addOnData.total_unit = editingAddOn.total_unit;
        addOnData.unit = editingAddOn.unit;
      }
    }

    if (isNewAddOn) {
      onCreate(addOnData);
    } else {
      onUpdate(editingAddOn.id, addOnData);
    }

    setEditingAddOn(null);
    setIsNewAddOn(false);
  };

  const handleFieldChange = (field, value) => {
    setEditingAddOn(prev => ({ ...prev, [field]: value }));
  };

  const getPricingTypeIcon = (pricingType) => {
    switch (pricingType) {
      case 'total_unit': return 'package';
      case 'per_user': return 'user';
      case 'per_org': return 'briefcase';
      case 'fixed': return 'tag';
      default: return 'dollar-sign';
    }
  };

  const getPricingTypeColor = (pricingType) => {
    switch (pricingType) {
      case 'total_unit': return 'blue';
      case 'per_user': return 'purple';
      case 'per_org': return 'green';
      case 'fixed': return 'orange';
      default: return 'gray';
    }
  };

  const formatPrice = (addOn) => {
    const symbol = addOn.currency?.symbol || '$';

    if (addOn.type === 'one_time') {
      if (addOn.price_min) {
        return `From ${symbol}${addOn.price_min}`;
      }
      return `${symbol}${addOn.price || 0}`;
    }

    if (addOn.total_unit) {
      return `${symbol}${addOn.price}/${addOn.total_unit} ${addOn.unit || 'unit'}/${addOn.interval || 'month'}`;
    }

    return `${symbol}${addOn.price}/${addOn.interval || 'month'}`;
  };

  const filteredAddOns = addOnKeys.filter(key => {
    const addOn = addOnsList[key];
    const name = (addOn.name || '').toLowerCase();
    const featureKey = (addOn.feature_key || '').toLowerCase();
    const search = searchTerm.toLowerCase();
    return name.includes(search) || featureKey.includes(search) || key.toLowerCase().includes(search);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="relative flex-1 max-w-md">
          <Icon image="search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
            placeholder="Search add-ons..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Button
          text="Add New Add-on"
          icon="plus"
          color="green"
          disabled={true}
          tooltip="Adding new add-ons is disabled for now."
        // action={handleAddAddOn}
        />
      </div>

      {/* Add-on Editor */}
      {editingAddOn && (
        <Card>
          <div className={Style.settingsSection}>
            <h3 className={Style.settingsHeader}>
              {isNewAddOn ? 'Add New Add-on' : 'Edit Add-on'}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Basic Info */}
              <div className={Style.settingsCard}>
                <label className={Style.settingsLabel}>Name *</label>
                <input
                  type="text"
                  className={Style.settingsInput}
                  value={editingAddOn.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  placeholder="e.g., Additional Storage"
                />
              </div>

              <div className={Style.settingsCard}>
                <label className={Style.settingsLabel}>Feature Key *</label>
                <select
                  className={Style.settingsInput}
                  value={editingAddOn.feature_key}
                  onChange={(e) => handleFieldChange('feature_key', e.target.value)}
                >
                  <option value="">Select a feature...</option>
                  {(availableFeatures || []).map(feature => (
                    <option key={feature.value || feature.key} value={feature.value || feature.key}>
                      {feature.label || feature.name} ({feature.value || feature.key})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">Links to plan feature for limits</p>
              </div>

              <div className={Style.settingsCard + " md:col-span-2 lg:col-span-3"}>
                <label className={Style.settingsLabel}>Description</label>
                <input
                  type="text"
                  className={Style.settingsInput}
                  value={editingAddOn.description}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  placeholder="e.g., Increase your file storage quota."
                />
              </div>

              {/* Pricing Configuration */}
              <div className={Style.settingsCard}>
                <label className={Style.settingsLabel}>Billing Type</label>
                <select
                  className={Style.settingsInput}
                  value={editingAddOn.type === 'one_time' ? 'one_time' : 'recurring'}
                  onChange={(e) => handleFieldChange('type', e.target.value)}
                >
                  <option value="recurring">Recurring</option>
                  <option value="one_time">One-time</option>
                </select>
              </div>

              <div className={Style.settingsCard}>
                <label className={Style.settingsLabel}>Pricing Type</label>
                <select
                  className={Style.settingsInput}
                  value={editingAddOn.pricing_type}
                  onChange={(e) => handleFieldChange('pricing_type', e.target.value)}
                >
                  {PRICING_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {editingAddOn.type !== 'one_time' && (
                <div className={Style.settingsCard}>
                  <label className={Style.settingsLabel}>Billing Interval</label>
                  <select
                    className={Style.settingsInput}
                    value={editingAddOn.interval}
                    onChange={(e) => handleFieldChange('interval', e.target.value)}
                  >
                    {INTERVAL_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Price */}
              <div className={Style.settingsCard}>
                <label className={Style.settingsLabel}>
                  {editingAddOn.pricing_type === 'fixed' && editingAddOn.type === 'one_time'
                    ? 'Minimum Price'
                    : 'Price'}
                </label>
                <input
                  type="number"
                  className={Style.settingsInput}
                  value={editingAddOn.pricing_type === 'fixed' && editingAddOn.type === 'one_time'
                    ? (editingAddOn.price_min ?? '')
                    : (editingAddOn.price ?? '')}
                  onChange={(e) => {
                    const value = e.target.value ? parseFloat(e.target.value) : 0;
                    if (editingAddOn.pricing_type === 'fixed' && editingAddOn.type === 'one_time') {
                      handleFieldChange('price_min', value);
                    } else {
                      handleFieldChange('price', value);
                    }
                  }}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>

              {/* Per Unit Fields */}
              {(editingAddOn.pricing_type === 'total_unit' || editingAddOn.pricing_type === 'per_user') && (
                <>
                  <div className={Style.settingsCard}>
                    <label className={Style.settingsLabel}>Per Unit Amount</label>
                    <input
                      type="number"
                      className={Style.settingsInput}
                      value={editingAddOn.total_unit ?? 1}
                      onChange={(e) => handleFieldChange('total_unit', parseInt(e.target.value) || 1)}
                      placeholder="1"
                      min="1"
                    />
                    <p className="text-xs text-slate-500 mt-1">Amount per unit (e.g., 100 MB)</p>
                  </div>

                  <div className={Style.settingsCard}>
                    <label className={Style.settingsLabel}>Unit Label</label>
                    <input
                      type="text"
                      className={Style.settingsInput}
                      value={editingAddOn.unit || ''}
                      onChange={(e) => handleFieldChange('unit', e.target.value)}
                      placeholder="e.g., MB, users, jobs"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="flex gap-2 mt-6">
              <Button
                text="Cancel"
                color="gray"
                outline
                action={handleCancelEdit}
              />
              <Button
                text={isNewAddOn ? 'Create Add-on' : 'Save Changes'}
                color="green"
                loading={loading}
                action={handleSaveAddOn}
                disabled={!editingAddOn.name || !editingAddOn.feature_key}
              />
            </div>
          </div>
        </Card>
      )}

      {/* Add-ons Grid */}
      <div className={Style.addOnsGrid}>
        {filteredAddOns.length > 0 ? (
          filteredAddOns.map((addOnKey) => {
            const addOn = addOnsList[addOnKey];
            return (
              <Card key={addOnKey} className={Style.addOnCard}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className={Style.addOnName}>{addOn.name}</h4>
                    <p className={Style.addOnMeta}>{addOn.description}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      icon="edit-2"
                      size="small"
                      color="blue"
                      outline
                      action={() => handleEditAddOn(addOnKey)}
                    />
                    <Button
                      icon="trash-2"
                      size="small"
                      color="red"
                      outline
                      action={() => onDelete(addOn.id || addOnKey)}
                    />
                  </div>
                </div>

                <div className={Style.addOnPrice}>{formatPrice(addOn)}</div>

                <div className="flex flex-wrap gap-2 mt-3">
                  <div className={Style.addOnTag}>
                    <Icon
                      image={getPricingTypeIcon(addOn.pricing_type)}
                      color={getPricingTypeColor(addOn.pricing_type)}
                      size={12}
                    />
                    <span>{PRICING_TYPES.find(t => t.value === addOn.pricing_type)?.label || addOn.pricing_type}</span>
                  </div>

                  <div className={Style.addOnTag}>
                    <Icon image={addOn.type === 'one_time' ? 'zap' : 'refresh-cw'} color="gray" size={12} />
                    <span>{addOn.type === 'one_time' ? 'One-time' : 'Recurring'}</span>
                  </div>

                  {addOn.feature_key && (
                    <div className={Style.addOnTag}>
                      <Icon image="link" color="gray" size={12} />
                      <span>{addOn.feature_key}</span>
                    </div>
                  )}

                  {addOn.meter && (
                    <div className={Style.addOnTag}>
                      <Icon image="activity" color="purple" size={12} />
                      <span>Metered</span>
                    </div>
                  )}
                </div>

                {addOn.product_id && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <p className="text-xs text-slate-400">
                      Product: {addOn.product_id}
                    </p>
                  </div>
                )}
              </Card>
            );
          })
        ) : (
          <Card>
            <div className={Style.emptyState}>
              <Icon image="package" size={48} className="mx-auto mb-4 text-slate-300" />
              <p className={Style.emptyStateText}>
                {searchTerm ? `No add-ons found matching "${searchTerm}"` : 'No add-ons configured yet'}
              </p>
              {!searchTerm && (
                <Button
                  text="Add Your First Add-on"
                  icon="plus"
                  color="green"
                  action={handleAddAddOn}
                />
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}